import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo, getSiteUrl } from "@/hooks/useSeo";
import { hitmanChapters, type Chapter } from "@/mocks/chapters";
import { otherChapters } from "@/mocks/chaptersOthers";
import { mangaList } from "@/mocks/mangaList";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ChapterNav from "./components/ChapterNav";
import ChapterListPanel from "./components/ChapterListPanel";
import RelatedManga from "./components/RelatedManga";

const chaptersByManga: Record<string, Chapter[]> = {
  "hitman-reborn": hitmanChapters,
  ...otherChapters,
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const chapter = id ? Object.values(chaptersByManga).flat().find((c) => c.id === id) : undefined;
  const manga = chapter ? mangaList.find((m) => m.id === chapter.mangaId) : undefined;
  const list = chapter ? (chaptersByManga[chapter.mangaId] ?? []).slice().sort((a, b) => a.index - b.index) : [];

  const jsonLd = useMemo(() => {
    if (!chapter || !manga) return [];
    const url = getSiteUrl(`/article/${chapter.id}`);
    return [
      {
        "@context": "https://storyvault3.github.io",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: getSiteUrl("/") },
          { "@type": "ListItem", position: 2, name: manga.title, item: getSiteUrl(`/category?search=${encodeURIComponent(manga.title)}`) },
          { "@type": "ListItem", position: 3, name: chapter.title, item: url },
        ],
      },
      {
        "@context": "https://storyvault3.github.io",
        "@type": "Article",
        headline: `${manga.title}漫画${chapter.title}`,
        description: `${manga.title}漫画${chapter.title}免费在线阅读,土豪漫画网高清画质、每日更新,为您提供沉浸式的追更体验。`,
        image: manga.cover,
        datePublished: chapter.publishedAt,
        author: { "@type": "Organization", name: "土豪漫画网" },
        publisher: { "@type": "Organization", name: "土豪漫画网" },
        mainEntityOfPage: url,
        inLanguage: "zh-CN",
      },
    ];
  }, [chapter, manga]);

  useSeo({
    title: chapter && manga ? `${manga.title}漫画${chapter.title}-免费阅读-土豪漫画网` : "章节不存在-土豪漫画网",
    description: chapter && manga
      ? `${manga.title}漫画${chapter.title}免费在线阅读,在土豪漫画网即可畅快追更,高清画质、每日更新。`
      : "你访问的章节不存在,请返回土豪漫画网首页继续浏览。",
    keywords: `${manga?.title ?? ""},${chapter?.title ?? ""},免费阅读,土豪漫画网`,
    path: `/article/${id ?? ""}`,
    jsonLd,
  });

  if (!chapter || !manga) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "章节不存在" }]} />
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">这个章节不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">可能链接已失效,去看看其他精彩漫画吧。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: manga.title, to: `/category?search=${encodeURIComponent(manga.title)}` }, { label: chapter.title }]} />

      <div className="mt-6 flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:p-6">
        <div className="relative h-40 w-28 shrink-0 overflow-hidden rounded-lg">
          <img
            src={manga.cover}
            alt={`${manga.title}漫画封面`}
            title={`${manga.title}漫画免费阅读`}
            className="h-full w-full object-cover object-top"
          />
          <span className="absolute left-2 top-2 rounded-full bg-foreground-950/60 px-2 py-0.5 text-xs text-background-50">
            {manga.status}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-primary-600">
            <i className="ri-book-2-line" aria-hidden="true" />
            {manga.titleEn}
          </p>
          <h1 className="mt-1.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {manga.title}漫画 {chapter.title}
          </h1>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{manga.description}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line text-foreground-400" aria-hidden="true" />
              {manga.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-foreground-400" aria-hidden="true" />
              {chapter.views} 阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-foreground-400" aria-hidden="true" />
              {chapter.publishedAt} 更新
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-secondary-500" aria-hidden="true" />
              {manga.rating} 分
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {manga.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category?search=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
        <a
          href="#chapter-list"
          className="flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-accent-200 bg-accent-100 px-4 py-2.5 text-sm font-medium text-accent-800 transition-colors hover:bg-accent-500 hover:text-background-50"
        >
          <i className="ri-list-unordered" aria-hidden="true" />
          查看全部章节
        </a>
      </div>

      <div className="mx-auto mt-8 max-w-3xl">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <ChapterNav chapter={chapter} list={list} />

          <div className="mt-8">
            <p className="text-sm font-medium text-foreground-400">土豪漫画网 · 免费阅读</p>
            <div className="mt-5 flex flex-col gap-6">
              {chapter.content.map((paragraph, index) => (
                <p
                  key={`${chapter.id}-p-${index}`}
                  className="text-base leading-8 text-foreground-800 first-letter:text-2xl first-letter:font-bold first-letter:text-primary-600"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-background-200 pt-5">
            <ChapterNav chapter={chapter} list={list} />
          </div>
        </div>

        <div id="chapter-list" className="mt-8 scroll-mt-24">
          <ChapterListPanel mangaTitle={manga.title} mangaId={manga.id} list={list} currentId={chapter.id} />
        </div>

        <div className="mt-12">
          <RelatedManga current={manga} />
        </div>
      </div>
    </article>
  );
}